import { useState, useRef } from "react";
import api, { formatApiError, resizeImageToBase64 } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Camera, CalendarClock, CheckCircle2, AlertTriangle, MapPin, UserMinus, RotateCcw, Unlock } from "lucide-react";

export function MaintenanceModal({ open, onOpenChange, maintenance, onChanged, onReport, isAdmin }) {
  const [newDate, setNewDate] = useState(maintenance?.date || "");
  const [loading, setLoading] = useState(false);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const releaseBtnRef = useRef(null);

  if (!maintenance) return null;
  const isCompleted = maintenance.status === "completed";
  const todayIso = new Date().toISOString().slice(0, 10);
  const isFuture = maintenance.date > todayIso;

  const reopen = async (reset) => {
    setLoading(true);
    try {
      await api.post(`/maintenances/${maintenance.id}/reopen`, { reset });
      toast.success(reset ? "Entretien réinitialisé." : "Entretien rouvert pour modification.");
      onChanged();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const reschedule = async () => {
    if (!newDate || newDate === maintenance.date) return;
    setLoading(true);
    try {
      await api.patch(`/maintenances/${maintenance.id}/reschedule`, { date: newDate });
      toast.success("Date de l'entretien modifiée.");
      onChanged();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const complete = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const b64 = await resizeImageToBase64(file);
      await api.post(`/maintenances/${maintenance.id}/complete`, { proof_photo: b64 });
      toast.success("Entretien terminé avec succès.");
      onChanged();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
      e.target.value = "";
    }
  };

  const completeKeepingPhoto = async () => {
    setLoading(true);
    try {
      await api.post(`/maintenances/${maintenance.id}/complete`, {});
      toast.success("Entretien terminé avec succès.");
      onChanged();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const release = async () => {
    setLoading(true);
    try {
      await api.post(`/maintenances/${maintenance.id}/unassign`);
      toast.success("Tâche libérée. Un autre usager peut maintenant la prendre.");
      setReleaseOpen(false);
      onChanged();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="maintenance-modal"
        onOpenAutoFocus={(e) => {
          if (releaseBtnRef.current) {
            e.preventDefault();
            releaseBtnRef.current.focus();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            {maintenance.park_name}
            {isCompleted ? (
              <Badge className="bg-primary/15 text-primary border-0">Terminé</Badge>
            ) : (
              <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-0">Assigné</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-1 text-sm text-muted-foreground">
          <p className="flex items-center gap-2"><MapPin className="h-4 w-4" /> Entretien prévu le {maintenance.date}</p>
          {isCompleted && maintenance.completed_at && (
            <p className="flex items-center gap-2 text-primary">
              <CheckCircle2 className="h-4 w-4" /> Terminé le {new Date(maintenance.completed_at).toLocaleString("fr-CA")}
            </p>
          )}
        </div>

        {isCompleted && maintenance.proof_photo && (
          <div className="rounded-xl overflow-hidden border border-border">
            <img src={maintenance.proof_photo} alt="Preuve" className="w-full max-h-60 object-cover" />
          </div>
        )}

        {!isCompleted && (
          <div className="space-y-5 pt-1">
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><CalendarClock className="h-4 w-4" /> Modifier la date</Label>
              <div className="flex gap-2">
                <Input
                  type="date"
                  data-testid="reschedule-date-input"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                />
                <Button data-testid="reschedule-btn" variant="secondary" onClick={reschedule} disabled={loading || newDate === maintenance.date}>
                  Changer
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-2 pt-1">
              {isFuture ? (
                <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
                  Cet entretien est prévu le {maintenance.date}. Vous pourrez le terminer à partir de cette date.
                </div>
              ) : maintenance.proof_photo ? (
                <>
                  <div className="rounded-xl overflow-hidden border border-border">
                    <img src={maintenance.proof_photo} alt="Photo existante" className="w-full max-h-44 object-cover" />
                  </div>
                  <Button data-testid="complete-keep-photo-btn" onClick={completeKeepingPhoto} disabled={loading} className="active:scale-95 transition-transform">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                    Conserver la photo et terminer
                  </Button>
                  <Button asChild variant="outline" disabled={loading}>
                    <label data-testid="complete-new-photo-btn" className="cursor-pointer">
                      <Camera className="h-4 w-4 mr-2" />
                      Prendre une nouvelle photo
                      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={complete} />
                    </label>
                  </Button>
                </>
              ) : (
                <Button asChild className="active:scale-95 transition-transform" disabled={loading}>
                  <label data-testid="complete-maintenance-btn" className="cursor-pointer">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Camera className="h-4 w-4 mr-2" />}
                    Terminer (prendre une photo)
                    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={complete} />
                  </label>
                </Button>
              )}
              <Button
                data-testid="open-report-problem-btn"
                variant="outline"
                onClick={() => { onOpenChange(false); onReport(maintenance); }}
                className="border-destructive/40 text-destructive hover:bg-destructive/10"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Rapporter un problème
              </Button>
              <Button
                ref={releaseBtnRef}
                data-testid="release-maintenance-btn"
                variant="ghost"
                onClick={() => setReleaseOpen(true)}
                disabled={loading}
                className="text-muted-foreground"
              >
                <UserMinus className="h-4 w-4 mr-2" />
                Libérer la tâche
              </Button>
            </div>
          </div>
        )}

        {isCompleted && (
          <div className="grid grid-cols-1 gap-2">
            {isAdmin && (
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="reopen-maintenance-btn" variant="secondary" onClick={() => reopen(false)} disabled={loading}>
                  <Unlock className="h-4 w-4 mr-2" /> Ouvrir
                </Button>
                <Button data-testid="reset-maintenance-btn" variant="outline" onClick={() => reopen(true)} disabled={loading} className="border-destructive/40 text-destructive hover:bg-destructive/10">
                  <RotateCcw className="h-4 w-4 mr-2" /> Réinitialiser
                </Button>
              </div>
            )}
            <Button
              data-testid="open-report-problem-btn"
              variant="outline"
              onClick={() => { onOpenChange(false); onReport(maintenance); }}
              className="border-destructive/40 text-destructive hover:bg-destructive/10"
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Rapporter un problème
            </Button>
          </div>
        )}
      </DialogContent>

      <AlertDialog open={releaseOpen} onOpenChange={setReleaseOpen}>
        <AlertDialogContent data-testid="release-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Libérer cette tâche ?</AlertDialogTitle>
            <AlertDialogDescription>
              L'entretien de <strong>{maintenance.park_name}</strong> du {maintenance.date} ne vous sera plus assigné et redeviendra disponible pour les autres usagers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="release-confirm-btn" onClick={release}>Libérer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
